import $ from 'jquery'

/**
 * Filter prostih terminov (TR) — mobilni panel (< 768px, Tailwind `md`).
 * Markup: template-parts/home/filter-prosti-termini.php ([data-prosti-termini])
 * Stili:  src/styles/_prosti-termini.scss
 *
 * Radio inputi (prosto_oseb / prosti_dan / prosta_ura) so EN SAM nabor za desktop in mobilno;
 * ta modul jih na mobilnem samo prikaže v celozaslonskem panelu:
 *  - .pt-trigger  odpre panel (.pt-panel.is-open, zaklep scrolla, aria-expanded)
 *  - .pt-confirm  zapre panel in obdrži izbiro
 *  - .pt-close / Escape  zapre panel in POVRNE izbiro ob odprtju (preklic)
 *  - .pt-chips    pod gumbom izpiše izbrano št. oseb/dan/uro (vrednost != 'all'); klik na chip
 *                 skupino vrne na "Vsi" in sproži 'change' na tem radiu. Skupina brez radia 'all'
 *                 (OTP early bird: št. oseb je fiksno "2 osebi") je fiksna in chipa nima.
 *                 Checkbox prikazi_zasedene (tudi zasedene / čakalna lista) ima svoj chip; klik ga odznači.
 *                 Preklic (X / Escape) povrne tudi checkbox.
 *  - ob prehodu na desktop (resize) se odprt panel zapre, izbira ostane
 */

const MOBILE_MQ = '(max-width: 767px)'
const GROUPS = ['prosto_oseb', 'prosti_dan', 'prosta_ura']

const CHIP_CLASS = 'pt-chip inline-flex items-center gap-x-2 rounded-full bg-[#E6E6E6] pl-4 pr-3 py-2 text-[13px] leading-none text-black cursor-pointer select-none transition-colors duration-200 hover:bg-[#d9d9d9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue'
const CHIP_ICON = '<svg width="10" height="10" viewBox="0 0 18 18" aria-hidden="true"><path d="M2 2l14 14M16 2L2 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>'

function setup(root) {
	const trigger = root.querySelector('.pt-trigger')
	const panel = root.querySelector('.pt-panel')
	const chips = root.querySelector('.pt-chips')
	const closeBtn = root.querySelector('.pt-close')
	const confirmBtn = root.querySelector('.pt-confirm')
	if (!trigger || !panel || !chips) return

	const removeLabel = chips.dataset.removeLabel || ''
	const inputs = (name) => Array.from(root.querySelectorAll(`input[type="radio"][name="${name}"]`))
	const checked = (name) => inputs(name).find((i) => i.checked)
	const zasedene = root.querySelector('input[type="checkbox"][name="prikazi_zasedene"]')
	const setZasedene = (on) => {
		if (!zasedene || zasedene.checked === on) return
		zasedene.checked = on
		zasedene.dispatchEvent(new Event('change', { bubbles: true }))
	}
	const labelFor = (input) => {
		const label = input.closest('label')
		return label ? label.textContent.trim() : input.value
	}
	const select = (input) => {
		if (!input || input.disabled || input.checked) return
		input.checked = true
		input.dispatchEvent(new Event('change', { bubbles: true }))
	}

	/* ---------- chipi izbranih terminov ---------- */

	const addChip = (group, text) => {
		const chip = document.createElement('button')
		chip.type = 'button'
		chip.className = CHIP_CLASS
		chip.dataset.group = group
		chip.setAttribute('aria-label', removeLabel ? `${removeLabel}: ${text}` : text)
		const span = document.createElement('span')
		span.textContent = text
		chip.appendChild(span)
		chip.insertAdjacentHTML('beforeend', CHIP_ICON)
		chips.appendChild(chip)
	}

	const renderChips = () => {
		chips.innerHTML = ''
		GROUPS.forEach((name) => {
			const input = checked(name)
			if (!input || input.value === 'all') return
			// fiksna skupina (brez "Vsi", npr. št. oseb pri OTP): ni kaj odstraniti, chipa ne izpišemo
			if (!inputs(name).some((i) => i.value === 'all')) return
			addChip(name, labelFor(input))
		})
		if (zasedene && zasedene.checked) addChip('prikazi_zasedene', labelFor(zasedene))
		chips.hidden = !chips.children.length
	}

	chips.addEventListener('click', (e) => {
		const chip = e.target.closest('.pt-chip')
		if (!chip) return
		if (chip.dataset.group === 'prikazi_zasedene') setZasedene(false)
		else select(inputs(chip.dataset.group).find((i) => i.value === 'all'))
		renderChips()
		trigger.focus({ preventScroll: true })
	})

	$(root).on('change', (e) => {
		if (e.target instanceof HTMLInputElement && (e.target.type === 'radio' || e.target === zasedene)) renderChips()
	})

	/* ---------- odpiranje / zapiranje panela ---------- */

	let snapshot = null // vrednosti ob odprtju (za preklic)
	const isOpen = () => panel.classList.contains('is-open')

	const open = () => {
		if (isOpen()) return
		snapshot = { groups: GROUPS.map((name) => (checked(name) || {}).value), zasedene: !!(zasedene && zasedene.checked) }
		panel.classList.add('is-open')
		panel.setAttribute('role', 'dialog')
		panel.setAttribute('aria-modal', 'true')
		panel.setAttribute('aria-label', trigger.textContent.trim())
		trigger.setAttribute('aria-expanded', 'true')
		document.documentElement.classList.add('pt-panel-open')
		panel.scrollTop = 0
		;(closeBtn || panel).focus({ preventScroll: true })
	}

	const close = ({ cancel = false, refocus = true } = {}) => {
		if (!isOpen()) return
		if (cancel && snapshot) {
			GROUPS.forEach((name, i) => {
				const group = inputs(name)
				const previous = group.find((input) => input.value === snapshot.groups[i] && !input.disabled)
				select(previous || group.find((input) => input.value === 'all'))
			})
			setZasedene(snapshot.zasedene)
		}
		snapshot = null
		panel.classList.remove('is-open')
		panel.removeAttribute('role')
		panel.removeAttribute('aria-modal')
		panel.removeAttribute('aria-label')
		trigger.setAttribute('aria-expanded', 'false')
		document.documentElement.classList.remove('pt-panel-open')
		renderChips()
		if (refocus) trigger.focus({ preventScroll: true })
	}

	trigger.addEventListener('click', open)
	if (closeBtn) closeBtn.addEventListener('click', () => close({ cancel: true }))
	if (confirmBtn) confirmBtn.addEventListener('click', () => close())

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && isOpen()) close({ cancel: true })
	})

	// prehod na desktop: panel je tam inline, zato odprto stanje zapremo (izbira ostane)
	const mq = window.matchMedia(MOBILE_MQ)
	const onMq = (e) => { if (!e.matches) close({ refocus: false }) }
	if (mq.addEventListener) mq.addEventListener('change', onMq)
	else if (mq.addListener) mq.addListener(onMq)

	renderChips()
}

export default {
	init() {
		document.querySelectorAll('[data-prosti-termini]').forEach(setup)
	},
}
